// src/pages/modules/InventoryLogPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MoreHorizontal, Eye, Loader2, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { usePermissions } from 'hooks/usePermissions';
// import usePermissionSocket from 'src/hooks/usePermissionSocket';
import LogDataDisplay from '../../components/common/LogDataDisplay';
import {
  fetchActivityLogs,
  clearError,
} from '../../store/slices/activityLogSlice';
import { formatDateTime } from 'utlis/formateDateTime';
import usePermissionSocket from 'hooks/usePermissionSocket';

const InventoryLogPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { logs, pagination, isLoading, error } = useSelector((state) => state.activityLog);

  const { canRead, canExport } = usePermissions('inventory_log');

  // Pagination & Search State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset to page 1 when searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with resource=inventory filter using activity log API
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchActivityLogs({
        page,
        page_size: pageSize,
        search: searchTerm,
        resource: 'inventory'  // Only inventory logs
      }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load inventory logs');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getUserName = (log) => {
    if (!log.created_by) return 'System';
    const { first_name = '', last_name = '', code = '' } = log.created_by || {};
    return `${first_name} ${last_name}`.trim() || code || 'Unknown User';
  };

  const getUserCode = (log) => {
    return log.created_by?.code || 'N/A';
  };

  const getResourceBadge = (resource) => {
    const map = {
      category: 'default',
      sub_category: 'secondary',
      inventory: 'outline',
      'inventory stocks': 'info',
      'inventory stock': 'info',
    };
    const label = resource?.replace(/_/g, ' ') || 'unknown';
    return <Badge variant={map[resource] || 'default'}>{label}</Badge>;
  };

  const getActionBadge = (type) => {
    const variants = {
      create: 'success',
      add: 'success',
      update: 'default',
      delete: 'destructive',
      remove: 'destructive',
    };
    return <Badge variant={variants[type] || 'secondary'}>{(type || '').toUpperCase()}</Badge>;
  };

  // Beautiful human-readable summary
  const generateSummary = (log) => {
    const user = getUserName(log);
    const item = log.data?.name || log.data?.item_name || 'item';
    const resource = log.resource_display || log.resource;

    switch (log.resource) {
      case 'category':
        return log.action_type === 'create'
          ? `${user} created category "${log.data?.name || ''}"`
          : `${user} updated category "${log.data?.old_name || log.data?.name || ''}" → "${log.data?.name || ''}"`;

      case 'sub_category':
        return `${user} ${log.action_type === 'create' ? 'created' : 'updated'} sub-category "${log.data?.name || ''}"`;

      case 'inventory':
        if (log.action_type === 'create') return `${user} added new inventory item "${item}"`;
        if (log.action_type === 'update') return `${user} updated inventory item "${item}"`;
        if (log.action_type === 'delete') return `${user} deleted inventory item "${item}"`;
        return `${user} modified inventory item "${item}"`;

      case 'inventory stock':
      case 'inventory stocks':
        const qty = log.data?.quantity || log.data?.qty;
        const type = log.action_type === 'add' ? 'added' : 'removed';
        return `${user} ${type} ${qty} unit(s) of "${item}" ${type === 'added' ? 'to' : 'from'} stock`;

      default:
        return `${user} performed "${log.action_type}" on ${resource}`;
    }
  };

  // Transform log data to match the expected format for LogDataDisplay
  const transformLogData = (log) => {
    if (!log.data) return null;
    
    // If the data already has the expected format, return as is
    if (log.data.old || log.data.new || log.data.changes) {
      return log.data;
    }
    
    // Otherwise, transform the flat data structure to the expected format
    // For create actions, put everything in 'new'
    if (log.action_type === 'create') {
      return {
        new: log.data,
        old: {},
        changes: {}
      };
    }
    
    // For delete actions, put everything in 'old'
    if (log.action_type === 'delete') {
      return {
        old: log.data,
        new: {},
        changes: {}
      };
    }
    
    // For update actions, try to determine what changed
    return {
      old: log.data.old || {},
      new: log.data.new || {},
      changes: log.data.changes || {}
    };
  };

  const handleViewDetails = (log) => {
    if (!canRead) {
      toast.error("You don't have permission to view details");
      return;
    }
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handleExport = () => {
    if (!canExport) {
      toast.error("You don't have permission to export");
      return;
    }
    toast.info("Export feature coming soon");
  };

  const columns = [
    {
      accessorKey: 'resource_display',
      header: 'Resource',
      cell: ({ row }) => getResourceBadge(row.original.resource_display || row.original.resource),
    },
    {
      accessorKey: 'action_type',
      header: 'Action',
      cell: ({ row }) => getActionBadge(row.original.action_type),
    },
    {
      accessorKey: 'created_by',
      header: 'Performed By',
      cell: ({ row }) => <span className="font-medium">{getUserName(row.original)}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Timestamp',
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            <div>{formatDateTime(row.original.created_at)}</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewDetails(row.original)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Inventory Activity Logs"
        description="Complete audit trail of inventory, categories & stock changes"
      />

      {/* {isLoading && logs.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          <span className="text-lg">Loading inventory logs...</span>
        </div>
      )} */}

      {!canRead && !isLoading && (
        <div className="text-center py-16 text-muted-foreground text-lg">
          You don't have permission to view inventory logs.
        </div>
      )}

      {canRead && (
        <DataTable
          data={logs}
          totalCount={pagination.count}
          columns={columns}
          searchPlaceholder="Search by resource, action, performed by..."
          showExport={false}
          onExport={handleExport}
          showImport={false}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.total_pages || 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
        />
      )}

      {/* Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Inventory Log Details</DialogTitle>
            <DialogDescription>
              {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-8 py-4">
                {/* Summary Card */}
                <div className="bg-card border rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-muted-foreground">Performed by</p>
                      <p className="text-lg font-semibold">{getUserName(selectedLog)}</p>
                      <p className="text-sm text-muted-foreground">Code: {getUserCode(selectedLog)}</p>
                    </div>
                    <div className="text-right">
                      {getResourceBadge(selectedLog.resource_display || selectedLog.resource)}
                      <div className="mt-2">{getActionBadge(selectedLog.action_type)}</div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Summary</p>
                    <p className="mt-1 text-foreground">{generateSummary(selectedLog)}</p>
                  </div> */}
                </div>

                {/* Log Data Display - Reusable Component */}
                <LogDataDisplay logData={transformLogData(selectedLog)} />
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryLogPage;